<template>
  <div class="interview-management-container">
    <Board v-if="hasHmInterviewPermission" ref="dashboard"/>
    <Interview
      v-if="hasHmInterviewPermission"
      class="interview"
      ref="interview"
      :listData="searchResult"
      :loading="interviewListLoading"
      :listType="listType"
      :search="search"
      @search="searchInterviewRecords"
      @edit="handleEditInterview"
      @cancel="handleCancelInterview"
      @resend="handleResendInterview"
      @sendNew="handleSendNewInterview"
      @sendRating="handleSendRatingInterview"
      @confirmInterview="handleConfirmInterview"
      @viewGroupInterview="handleViewGroupInterview"
      @scrollBottom="handleScrollBottom"
      @listTypeChange="handleListTypeChange"
    />
    <Rate
      v-if="hasHMRatingPermission"
      class="rate"
      ref="rate"
      :listData="searchRateList"
      :loading="rateListLoading"
      :search="searchRateParams"
      @search="searchRateRecordList"
      @scrollBottom="handleRateScrollBottom"
      @sendRating="handleSendInviteRating"
    ></Rate>
    <InviteInterview
      ref="inviteDialog"
      :applicationList="inviteDialog.applicationList"
      :saveWithEvent="inviteDialog.saveWithEvent"
      :isEditingGroupInterview="isEditingGroupInterview"
      @save="handleSaveInterviewInvite"
      @sent="handleSentInterviewInvite"
    />
    <InterviewConfirmDialog
      ref="interviewConfirmDialog"
      :applicationList="interviewConfirmDialog.applicationList"
    />
  </div>
</template>

<script>
import InterviewConfirmDialog from '@/views/adminNew/applicantList/dialog/interviewConfirmDialog.vue'
import moment from 'moment'
import _ from 'lodash'
import { addI18nProperty } from '@/i18n'
import { mapActions } from 'vuex'
import Board from './board.vue'
import Interview from './interview.vue'
import Rate from './rate.vue'
import InviteInterview from '../applicantList/dialog/inviteInterview.vue'
import { INTERVIEW_CANCELED_STATUS, INTERVIEW_OPERATION_TYPE, INTERVIEW_STATUS, LIST_TYPE, RATE_OPERATE_STATUS } from '@/constant/interview'
import { openNewBrowserWindowByUrl } from '@/utils/common'
import {
  buildNewTemplateCandidateContainer,
  buildNewTemplateCandidateItem,
  normalizeInterviewEmailThemeColor,
  replaceNewTemplateCandidateSection
} from '@/utils/interviewEmail'

const INVITE_DIALOG_MODE = {
  CREATE: 'CREATE',
  EDIT: 'EDIT',
  RESEND: 'RESEND'
}

const INVITE_HM_RATE_EMAIL_TEMPLATE_TYPE = 'Invite HM Rate'

// 在邮件模板中使用的占位符，AI summary 将在这个位置插入
const AI_SUMMARY_TEMPLATE_FIELD = '$' + '{candidate_info_table}'

export default {
  components: {
    InterviewConfirmDialog,
    Board,
    Interview,
    InviteInterview,
    Rate
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      searchResult: [],
      interviewList: [],
      interviewListLoading: false,
      interviewConfirmDialog: {
        show: false,
        applicationList: []
      },
      inviteDialog: {
        mode: INVITE_DIALOG_MODE.CREATE,
        saveWithEvent: false,
        originInvitation: null,
        invitation: null,
        applicationList: []
      },
      pagination: {
        page: 1,
        totalPage: 0
      },
      listType: LIST_TYPE.TABLE,
      search: {
        keywords: null,
        status: [INTERVIEW_STATUS.SCHEDULED, INTERVIEW_STATUS.FINISHED, INTERVIEW_STATUS.PENDING, INTERVIEW_STATUS.PARTIALLY_RATED],
        dayRange: [],
        selectDate: moment().format('YYYY-MM-DD')
      },
      hasMicrosoftGraphAuthToken: false,
      microsoftGraphAuthUrl: null,
      microsoftGraphEventId: null,
      rateRecordList: [],
      searchRateList: [],
      rateListLoading: false,
      ratePagination: {
        page: 1,
        totalPage: 0
      },
      searchRateParams: {
        keywords: null,
        status: [RATE_OPERATE_STATUS.PENDING, RATE_OPERATE_STATUS.FINISHED]
      },
      invitationSubjectEn: null,
      invitationSubjectCn: null,
      invitationContentEn: null,
      invitationContentCn: null,
      emailThemeColor: '#0096cc'
    }
  },
  computed: {
    hasAISummaryPermission () {
      return this.hasAnyPermission('PREMIUM_AI_SUMMARY')
    },
    hasAIResumeMatchPermission () {
      return this.hasAnyPermission(['PREMIUM_AI_RESUME_MATCH', 'PERM_HR_VIEW_AI_FIT_SCORE'])
    },
    hasOnlyActionOnMyCandidatePermission () {
      return this.hasAnyPermission('PERM_ONLY_ACTION_ON_MY_CANDIDATE')
    },
    hasHMRatingPermission () {
      return this.hasAnyPermission('PREMIUM_INVITE_HM_REVIEW')
    },
    hasHmInterviewPermission () {
      return this.hasAnyPermission(['PERM_HR_INTERVIEW_SCHEDULING', 'PREMIUM_INTERVIEW_SCHEDULING'])
    },
    isEditingGroupInterview () {
      return this.inviteDialog.mode === INVITE_DIALOG_MODE.EDIT && _.get(this.inviteDialog.originInvitation, 'is_group_interview') === true
    }
  },
  created () {
    this.initPage()
    this.loadRateRecordList()
  },
  methods: {
    ...mapActions('interview', [
      'searchInterviewRecordList',
      'editInvitation',
      'resendInvitation',
      'resendInvitationRatingLink',
      'getRateRecordList',
      'updateInterviewRecordInfo',
      'getInterviewRecordInfo'
    ]),
    ...mapActions('microsoftGraph', ['checkMicrosoftGraphAuth', 'deleteOnlineMeetingEvent']),
    ...mapActions('hm', ['resendInviteHm']),
    ...mapActions('notificationTemplate', ['getCompanyInviteHmRateEmailTemplate']),
    ...mapActions('application', ['getApplicationAISummary', 'getApplicationAIMatchInfo']),
    /**
     * 生成HM评分详情
     * @param {Array} scores - 评分列表
     * @param {Array} hireManagers - 招聘经理列表
     * @returns {Array} HM评分详情
     */
    generateHmResultDetails (scores, hireManagers) {
      return _.chain(scores)
        .filter(score => score.hm_id)
        .map(score => {
          const hm = _.find(hireManagers, { id: score.hm_id })
          return {
            hmName: hm.full_name,
            decision: score.submit_time ? score.decision : null,
            submitTime: score.submit_time
          }
        })
        .value()
    },
    initPage () {
      const keywords = this.$route.query.keyword
      if (keywords) {
        this.search.keywords = keywords
      }

      const status = this.$route.query.status || ''
      if (status) {
        const statusList = status.split(',')
        this.search.status = _.map(statusList, status => parseInt(status))
      }

      const listType = this.$route.query.list_type
      if (listType) {
        this.listType = listType
        const date = moment(this.search.selectDate).format('YYYY-MM-DD')
        const weekOfDay = moment(date, 'YYYY-MM-DD').format('E')
        const lastMonday = moment(date).subtract(weekOfDay - 1, 'days').format('YYYY-MM-DD')
        const lastSunday = moment(lastMonday).add(6, 'day').format('YYYY-MM-DD')
        this.search.dayRange[0] = lastMonday
        this.search.dayRange[6] = lastSunday

        const params = this.formatParams(this.search)
        this.loadInterviewList(params)
        return
      }

      this.loadInterviewList()
    },
    handleScrollBottom () {
      const search = this.search
      if (this.pagination.page === this.pagination.totalPage) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.noData`))
        return
      }
      this.pagination.page += 1
      const params = {
        page_index: this.pagination.page,
        keyword: search.keywords,
        status: _.join(search.status, ',')
      }
      this.loadInterviewList(params)
    },
    async loadInterviewList (searchParams) {
      if (!this.hasHmInterviewPermission) {
        return
      }
      this.interviewListLoading = true
      try {
        const params = {
          page_index: this.pagination.page,
          status: [INTERVIEW_STATUS.SCHEDULED, INTERVIEW_STATUS.PENDING, INTERVIEW_STATUS.FINISHED, INTERVIEW_STATUS.PARTIALLY_RATED].join(',')
        }
        const resp = await this.searchInterviewRecordList(searchParams || params)
        if (resp) {
          const vm = this
          this.pagination.totalPage = resp.page_count
          const interviewList = _.map(this.listType === LIST_TYPE.CALENDAR ? resp : resp.interview_records, interview => {
            const hasMultipleCandidates = interview.candidate && interview.candidate.length > 1
            addI18nProperty(interview, 'companyName', 'company_cn_name', 'company_name')
            addI18nProperty(interview, 'jobName', 'job_cn_title', 'job_title')
            const applicationList = _.map(interview.candidate, item => {
              return {
                candidate: item,
                job: {
                  id: interview.job_id,
                  cn_title: interview.job_cn_title,
                  title: interview.job_title,
                  company: {
                    cn_name: interview.company_cn_name,
                    name: interview.company_name,
                    id: interview.company_id,
                    root_id: interview.root_company_id
                  }
                }
              }
            })
            interview.applicationList = applicationList

            let statusClass = null
            if (interview.status === INTERVIEW_STATUS.PENDING) {
              statusClass = 'pending'
            } else if (interview.status === INTERVIEW_STATUS.SCHEDULED) {
              statusClass = 'scheduled'
            } else if (interview.status === INTERVIEW_STATUS.FINISHED) {
              statusClass = 'finished'
            } else if (_.includes(INTERVIEW_CANCELED_STATUS, interview.status)) {
              statusClass = 'canceled'
            } else if (interview.status === INTERVIEW_STATUS.PARTIALLY_RATED) {
              statusClass = 'partially-rated'
            }
            interview.statusClass = statusClass

            // 为顶级行也添加hmResultDetails（仅在单候选人场景下用于popover展示）
            const topHmResultDetails = !hasMultipleCandidates ? this.generateHmResultDetails(interview.interview_scores, interview.hire_manager) : []

            const needCalculateInterviewStatus = [INTERVIEW_STATUS.FINISHED, INTERVIEW_STATUS.PARTIALLY_RATED].includes(interview.status)

            let hireManager = interview.hire_manager
            if (interview.status === INTERVIEW_STATUS.PARTIALLY_RATED) {
              const scoresByHm = _.groupBy(interview.interview_scores, 'hm_id')
              hireManager = _.map(interview.hire_manager, hm => {
                const hmScores = scoresByHm[hm.id]
                const hasDecision = _.filter(hmScores, 'submit_time')
                const recordStatus = hasDecision.length === hmScores.length
                  ? INTERVIEW_STATUS.FINISHED
                  : (hasDecision.length === 0 ? INTERVIEW_STATUS.PENDING : INTERVIEW_STATUS.PARTIALLY_RATED)
                return {
                  ...hm,
                  record_status: recordStatus
                }
              })
            }

            return {
              ...interview,
              noAction: this.hasOnlyActionOnMyCandidatePermission && !interview.is_my_job,
              disabledTooltip: this.$t('admin.application.applicantList.message.disabledActionCandidateInJob'),
              hire_manager: hireManager,
              hmResultDetails: topHmResultDetails,
              get interviewDate () {
                if (!interview.interview_time) {
                  return '-'
                }
                const i18nTime = moment(interview.interview_time).locale(vm.$i18n.locale)
                const year = i18nTime.format('YYYY')
                const month = i18nTime.format('MMM')
                const weekday = i18nTime.format('dddd')
                const day = i18nTime.format('Do')
                return vm.$t(`${vm.i18nPrefix}.label.interviewDay`, { year, month, weekday, day })
              },
              get interviewTime () {
                if (!interview.interview_time) {
                  return '-'
                }
                const startTime = moment(interview.interview_time).format('HH:mm')
                const endTime = moment(interview.interview_end_time).format('HH:mm')
                return vm.$t(`${vm.i18nPrefix}.label.interviewTime`, { startTime, endTime })
              },
              get recruiter () {
                return interview.creator.full_name
              },
              get hiringManagerNames () {
                return _.chain(interview.hire_manager).map('full_name').join(', ').value()
              },
              get candidateNames () {
                return interview.is_group_interview
                  ? interview.group_name
                  : _.chain(interview.candidate).map('name').join(', ').value()
              },
              get children () {
                if (hasMultipleCandidates || interview.is_group_interview) {
                  return _.map(interview.candidate, candidate => {
                    // 获取该候选人的所有HM评分记录
                    const candidateScores = _.filter(interview.interview_scores, { candidate_id: candidate.id })

                    // 计算该候选人的面试结果
                    const submittedScores = candidateScores.filter(score => score.submit_time)
                    const hasScore = submittedScores.length > 0

                    let candidateInterviewStatus = interview.status
                    if (needCalculateInterviewStatus) {
                      if (hasScore) {
                        if (submittedScores.length === interview.hire_manager.length) {
                          candidateInterviewStatus = INTERVIEW_STATUS.FINISHED
                        } else {
                          candidateInterviewStatus = INTERVIEW_STATUS.PARTIALLY_RATED
                        }
                      } else {
                        candidateInterviewStatus = INTERVIEW_STATUS.PENDING
                      }
                    }

                    // 生成该候选人的HM评分详情
                    const candidateHmResultDetails = vm.generateHmResultDetails(candidateScores, interview.hire_manager)
                    const hireManager = _.map(interview.hire_manager, hm => {
                      const hmRecord = _.find(candidateScores, { hm_id: hm.id })
                      return {
                        ...hm,
                        record_status: hmRecord?.record_status
                      }
                    })
                    return {
                      ...interview,
                      uuid: `${interview.uuid}_${candidate.id}`,
                      is_group_interview: false,
                      candidate: [candidate],
                      candidateId: candidate.id,
                      hire_manager: hireManager,
                      recruiter: interview.creator.full_name,
                      hiringManagerNames: _.chain(interview.hire_manager).map('full_name').join(', ').value(),
                      candidateNames: candidate.name,
                      status: candidateInterviewStatus,
                      interview_decision: interview.candidate_interview_decision?.[candidate.id],
                      hmResultDetails: candidateHmResultDetails,
                      hiddenAction: true
                    }
                  })
                }
                return null
              }
            }
          })

          if (this.listType === LIST_TYPE.CALENDAR) {
            this.interviewList = interviewList
          } else if (this.pagination.page === 1) {
            this.interviewList = interviewList
          } else {
            this.interviewList.push(...interviewList)
          }
          this.searchResult = this.interviewList

          if (this.listType === LIST_TYPE.CALENDAR) {
            this.$nextTick(() => {
              this.$refs.interview.formatData()
            })
          }
        }
      } finally {
        this.interviewListLoading = false
      }
    },
    searchInterviewRecords () {
      const params = this.formatParams(this.search)
      this.loadInterviewList(params)
    },
    formatParams (search) {
      let params = null
      if (this.listType === LIST_TYPE.TABLE) {
        this.pagination.page = 1
        params = {
          page_index: this.pagination.page,
          keyword: search.keywords,
          status: _.join(search.status, ',')
        }
      } else {
        params = {
          start_day: search.dayRange[0],
          end_day: moment(_.last(search.dayRange)).add(1, 'd').format('YYYY-MM-DD'),
          keyword: search.keywords,
          status: _.join(search.status, ',')
        }
      }
      return params
    },
    handleListTypeChange (search) {
      this.listType = this.listType === LIST_TYPE.TABLE ? LIST_TYPE.CALENDAR : LIST_TYPE.TABLE
      const params = this.formatParams(this.search)
      this.$router.push({
        query: { ...this.$route.query, list_type: this.listType }
      })
      this.loadInterviewList(params)
    },
    async handleSaveInterviewInvite (invitation, callback) {
      let success = false
      try {
        const resp = await this.editInvitation({
          ...invitation,
          is_edit: this.inviteDialog.mode === INVITE_DIALOG_MODE.EDIT,
          uid: this.inviteDialog.originInvitation.uuid,
          original_hm_ids: _.map(this.inviteDialog.originInvitation.hire_manager, 'id'),
          has_other_candidate: Object.keys(_.groupBy(this.inviteDialog.originInvitation.candidate, 'id')).length > 1
        })
        if (resp) {
          success = resp.success
          const action = this.$t('common.btn.invite')
          if (success) {
            this.$message.success(this.$t('common.message.actionSuccess', { action }))
            this.interviewListLoading = true
            this.$nextTick(() => {
              setTimeout(() => {
                this.searchInterviewRecords()
              }, 500)
            })
          } else {
            this.$message.error(this.$t('common.message.actionFail', { action }))
          }
          this.$refs.dashboard.loadInterviewBoardInfo()
        }
      } finally {
        callback(success)
      }
    },
    handleSentInterviewInvite (inviteResult) {
      this.interviewListLoading = true
      this.$nextTick(() => {
        setTimeout(() => {
          this.searchInterviewRecords()
        }, 500)
      })
    },
    async handleEditInterview (record) {
      this.inviteDialog.applicationList = record.applicationList
      this.inviteDialog.saveWithEvent = true
      this.inviteDialog.mode = INVITE_DIALOG_MODE.EDIT
      this.inviteDialog.originInvitation = record
      this.inviteDialog.invitation = record
      this.$refs.inviteDialog.showDialog(record)
    },
    async handleResendInterview (record) {
      this.inviteDialog.applicationList = record.applicationList
      this.inviteDialog.saveWithEvent = true
      this.inviteDialog.mode = INVITE_DIALOG_MODE.RESEND
      this.inviteDialog.originInvitation = record
      this.inviteDialog.invitation = record
      this.$refs.inviteDialog.showDialog(record)
    },
    async checkCurrentAccountAuthGraph (callback) {
      if (this.microsoftGraphAuthWin && this.microsoftGraphAuthWin.closed) {
        this.microsoftGraphAuthWin = null
        this.microsoftGraphAuthUrl = null
        return
      }
      const resp = await this.checkMicrosoftGraphAuth()
      if (resp) {
        this.hasMicrosoftGraphAuthToken = false
        if (!this.microsoftGraphAuthUrl) {
          this.microsoftGraphAuthUrl = resp
          this.microsoftGraphAuthWin = openNewBrowserWindowByUrl(this.microsoftGraphAuthUrl, null, 500, 600)
        }
        setTimeout(() => this.checkCurrentAccountAuthGraph(callback), 1000)
      } else {
        this.microsoftGraphAuthUrl = null
        this.hasMicrosoftGraphAuthToken = true
        if (this.microsoftGraphAuthWin) {
          this.microsoftGraphAuthWin.close()
        }
        callback()
      }
    },
    async cancelInterview (record) {
      this.inviteDialog.applicationList = record.applicationList
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.updateInterviewRecordInfo({
          interviewUuid: record.uuid,
          data: {
            operation_type: INTERVIEW_OPERATION_TYPE.CANCEL
          }
        })
        if (resp) {
          const action = this.$t('common.btn.cancel')
          if (resp === 'success') {
            if (this.microsoftGraphEventId) {
              await this.deleteOnlineMeetingEvent({ eventId: this.microsoftGraphEventId })
            }
            record.status = INTERVIEW_STATUS.CANCELED_BY_ORGANIZER
            record.statusClass = 'canceled'
            this.$message.success(this.$t('common.message.actionSuccess', { action }))
          } else if (resp === 'cannot_cancel_interview_due_to_deadline') {
            this.$message.warning(this.$t(`${this.i18nPrefix}.message.cannotCancelInterviewDueToDeadline`))
          } else if (resp === 'cannot_cancel_interview_rated') {
            this.$message.warning(this.$t(`${this.i18nPrefix}.message.cannotCancelInterviewHasBeenRated`))
          } else {
            this.$message.error(this.$t('common.message.actionFail', { action }))
          }
          this.$refs.dashboard.loadInterviewBoardInfo()
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async handleCancelInterview (record) {
      this.microsoftGraphEventId = record.microsoft_graph_event_id
      if (this.microsoftGraphEventId) {
        await this.checkCurrentAccountAuthGraph(() => this.cancelInterview(record))
        if (!this.hasMicrosoftGraphAuthToken) {
          this.$message({
            type: 'warning',
            message: this.$t(`${this.i18nPrefix}.message.cannotCancelMicrosoftMeeting`)
          })
        }
      } else {
        await this.cancelInterview(record)
      }
    },
    async handleSendNewInterview (record) {
      this.inviteDialog.applicationList = record.applicationList
      this.inviteDialog.saveWithEvent = false
      this.inviteDialog.mode = INVITE_DIALOG_MODE.CREATE
      this.inviteDialog.originInvitation = null
      this.inviteDialog.invitation = null
      this.$refs.inviteDialog.showDialog()
    },
    async handleSendRatingInterview (record) {
      this.inviteDialog.applicationList = record.applicationList
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.resendInvitationRatingLink({
          uid: record.uuid,
          job_id: record.job_id
        })
        if (resp) {
          const action = this.$t('common.btn.send')
          if (resp === 'success') {
            this.$message.success(this.$t('common.message.actionSuccess', { action }))
          } else {
            this.$message.error(this.$t('common.message.actionFail', { action }))
          }
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    handleConfirmInterview (record) {
      this.interviewConfirmDialog.applicationList = record.applicationList
      this.$refs.interviewConfirmDialog.showDialog(record)
    },
    handleViewGroupInterview (record) {
      window.open(`/recruiter-pc/v-admin/application/group-interview?uuid=${encodeURIComponent(record.uuid)}`)
    },
    searchRateRecordList () {
      this.ratePagination.page = 1
      this.loadRateRecordList()
    },
    handleRateScrollBottom () {
      if (this.ratePagination.page === this.ratePagination.totalPage) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.noData`))
        return
      }
      this.ratePagination.page += 1
      this.loadRateRecordList()
    },
    async loadRateRecordList () {
      if (!this.hasHMRatingPermission) {
        return
      }
      this.rateListLoading = true
      try {
        const params = {
          page_index: this.ratePagination.page,
          keyword: this.searchRateParams.keywords,
          status: _.join(this.searchRateParams.status, ',')
        }
        const resp = await this.getRateRecordList(params)
        if (resp) {
          const vm = this
          this.ratePagination.totalPage = resp.total_page
          const rateRecordList = _.map(resp.invite_records, record => {
            addI18nProperty(record, 'companyName', 'company.cn_name', 'company.name')
            addI18nProperty(record, 'jobName', 'job.cn_title', 'job.title')

            const applicationList = [{
              candidate: {
                id: record.candidate.id,
                name: record.candidate.name
              },
              job: {
                ...record.job,
                company: {
                  cn_name: record.company.cn_name,
                  name: record.company.name,
                  id: record.company.id,
                  root_id: record.company.root_company_id
                }
              }
            }]

            record.applicationList = applicationList

            return {
              ...record,
              noAction: this.hasOnlyActionOnMyCandidatePermission && !record.job.is_my_job,
              disabledTooltip: this.$t('admin.application.applicantList.message.disabledActionCandidateInJob'),
              get inviteDate () {
                const i18nTime = moment(record.invite_time).locale(vm.$i18n.locale)
                const year = i18nTime.format('YYYY')
                const month = i18nTime.format('MMM')
                const weekday = i18nTime.format('dddd')
                const day = i18nTime.format('Do')
                return vm.$t(`${vm.i18nPrefix}.label.interviewDay`, { year, month, weekday, day })
              },
              get inviteTime () {
                return moment(record.invite_time).format('H:mm:ss')
              },
              get recruiter () {
                return record.creator.full_name
              },
              get hiringManagerNames () {
                return record.hm.full_name
              },
              get candidateNames () {
                return record.candidate.name
              }
            }
          })
          if (this.ratePagination.page === 1) {
            this.searchRateList = rateRecordList
          } else {
            this.searchRateList.push(...rateRecordList)
          }
        }
      } finally {
        this.rateListLoading = false
      }
    },
    async loadApplicationAISummary (record) {
      const params = {
        job_id: record.job.id,
        candidate_ids: `${record.candidate.id}`
      }
      const resp = await this.getApplicationAISummary(params)
      if (resp) {
        record.candidate.ai_summary_info = _.get(resp, record.candidate.id)
      }
    },
    async loadApplicationAIMatchInfo (record) {
      const params = {
        job_id: record.job.id,
        candidate_ids: `${record.candidate.id}`
      }
      const resp = await this.getApplicationAIMatchInfo(params)
      if (resp) {
        record.candidate.ai_match_info = _.get(resp, record.candidate.id)
      }
    },
    async loadEmailTemplate (record) {
      const resp = await this.getCompanyInviteHmRateEmailTemplate({
        email_template_type: INVITE_HM_RATE_EMAIL_TEMPLATE_TYPE,
        job_id: record.job.id
      })
      if (resp) {
        this.invitationSubjectEn = resp.subject_en
        this.invitationSubjectCn = resp.subject_cn
        this.invitationContentEn = resp.content_en
        this.invitationContentCn = resp.content_cn
        this.emailThemeColor = resp.theme_color || '#0096cc'
      }
    },
    /**
     * 对 email content 中加入 AI summary 和 AI Match 信息，替换掉 AI_SUMMARY_TEMPLATE_FIELD 占位符
     * @param record 评估记录
     * @param emailContent email content
     * @param languageCode 语言
     */
    getHasAISummaryEmailTemplateInfo (record, emailContent, languageCode) {
      if (!_.includes(emailContent, AI_SUMMARY_TEMPLATE_FIELD)) {
        return emailContent
      }
      const isEn = languageCode === 'en'
      const aiSummaryInfo = record.candidate.ai_summary_info
      const summary = _.get(aiSummaryInfo, isEn ? 'ai_summary_en' : 'ai_summary_cn') || ''
      const aiMatchInfo = record.candidate.ai_match_info
      const aiMatchScore = aiMatchInfo ? aiMatchInfo.resume_ai_match_score : ''
      const candidateId = record.candidate.id
      const candidateContent = buildNewTemplateCandidateItem({
        candidateId,
        candidateName: record.candidate.name,
        rateLink: '$' + '{rate_link_' + candidateId + '}',
        languageCode,
        themeColor: normalizeInterviewEmailThemeColor(this.emailThemeColor),
        showSummary: false,
        summary,
        allowGenerateSummary: false,
        showFitScore: false,
        fitScoreImageUrl: '',
        fitScoreValue: aiMatchScore,
        allowGenerateFitScore: false,
        showLoading: false
      })
      return replaceNewTemplateCandidateSection(
        emailContent,
        AI_SUMMARY_TEMPLATE_FIELD,
        buildNewTemplateCandidateContainer(languageCode, candidateContent),
        languageCode,
        this.emailThemeColor
      )
    },
    async handleSendInviteRating (record) {
      this.rateListLoading = true
      try {
        const languageCode = record.language_code
        await this.loadEmailTemplate(record)
        if (this.hasAISummaryPermission) {
          await this.loadApplicationAISummary(record)
        }
        if (this.hasAIResumeMatchPermission) {
          await this.loadApplicationAIMatchInfo(record)
        }
        let emailContent = languageCode === 'en' ? this.invitationContentEn : this.invitationContentCn
        emailContent = this.getHasAISummaryEmailTemplateInfo(record, emailContent, languageCode)
        const params = {
          hire_manager: record.hm,
          candidate_id: record.candidate.id,
          job_id: record.job.id,
          invite_code: record.invite_code,
          email_template: {
            subject: languageCode === 'en' ? this.invitationSubjectEn : this.invitationSubjectCn,
            content: emailContent,
            language_code: languageCode
          }
        }
        const resp = await this.resendInviteHm(params)
        if (resp) {
          this.$message({
            message: this.$t('common.message.inviteSuccess'),
            type: 'success'
          })
        }
      } finally {
        this.rateListLoading = false
      }
    }
  }
}
</script>

<style lang='scss' scoped>
.interview-management-container {
  display: flex;
  flex-direction: column;

  .interview {
    height: 100%;
  }
  .rate {
    height: 100%;
  }
}
</style>
